import { SocketEvent } from "@common/SocketEvents";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Modal,
  ModalProps,
  Slider,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { FC, SetStateAction, useEffect, useRef, useState } from "react";
import { useAppContext } from "../contexts/App.context";
import { useSocket } from "../contexts/Socket.context";
import { useAdmin } from "../contexts/useAdmin";
import AdminPinPad from "./AdminPinPad";
import AdminSection from "./AdminSection";
import DesktopControls from "./DesktopControls";


interface SettingsModalProps extends Omit<ModalProps, "children"> {}

interface SettingToggle {
  key: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  // Only the Go server enforces/understands this setting.
  serverPinOnly?: boolean;
}

const defaultSettings = {
  musicPlayControlLocked: false,
  playlistSelectLock: false,
  playlistDeleteLock: false,
  autoRemovePlayedTracks: true,
  skipDeleteThreshold: 0,
  administratorOverride: false,
  askWhoSing: false,
};

const SettingsModal: FC<SettingsModalProps> = (modalProps) => {
  const { roomId, sendRequest } = useSocket();
  const { data, setData } = useAppContext();
  const { administratorOverride } = data;
  const {
    playlistData: { playerSettings },
  } = data;
  const admin = useAdmin();

  // On servers with admin PIN support, closing settings locks an unlocked
  // device again, unless it stays admin.
  useEffect(() => {
    if (
      modalProps.open ||
      !admin.hasAdminPassword ||
      !admin.isAdmin ||
      admin.stayAdmin
    )
      return;
    admin.logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalProps.open, admin.hasAdminPassword, admin.isAdmin, admin.stayAdmin]);

  const {
    musicPlayControlLocked,
    showIdleScreen,
    playlistSelectLock,
    playlistDeleteLock,
    playlistSearchLock,
    autoRemovePlayedTracks,
    skipDeleteThreshold,
    askWhoSing,
    allowGuestPlayNext,
    allowGuestMoveUpDown,
    allowGuestMoveTopBottom,
  } = {
    ...defaultSettings,
    ...playerSettings,
  };

  // The lock settings are stored as "...Locked" (shared with the Nest server
  // and older remotes); here they read as what guests are allowed to do.
  const guestToggles: SettingToggle[] = [
    {
      key: "musicPlayControlLocked",
      label: "Play controls",
      description: "Play, pause and skip songs.",
      checked: !musicPlayControlLocked,
      onChange: (on) => handleUpdateSettings({ musicPlayControlLocked: !on }),
    },
    {
      key: "playlistSelectLock",
      label: "Pick songs",
      description: "Tap a song in the queue to play it now.",
      checked: !playlistSelectLock,
      onChange: (on) => handleUpdateSettings({ playlistSelectLock: !on }),
    },
    {
      key: "playlistSearchLock",
      label: "Add songs",
      description: "Search for songs and add them to the queue.",
      checked: !playlistSearchLock,
      onChange: (on) => handleUpdateSettings({ playlistSearchLock: !on }),
    },
    {
      key: "playlistDeleteLock",
      label: "Delete songs",
      description: "Remove songs from the queue.",
      checked: !playlistDeleteLock,
      onChange: (on) => handleUpdateSettings({ playlistDeleteLock: !on }),
    },
    {
      key: "allowGuestPlayNext",
      label: "Play Next",
      description: "Move a song to play right after the current one.",
      checked: !!allowGuestPlayNext,
      onChange: (on) => handleUpdateSettings({ allowGuestPlayNext: on }),
      serverPinOnly: true,
    },
    {
      key: "allowGuestMoveUpDown",
      label: "Move up / down",
      description: "Move a song one place up or down in the queue.",
      checked: !!allowGuestMoveUpDown,
      onChange: (on) => handleUpdateSettings({ allowGuestMoveUpDown: on }),
      serverPinOnly: true,
    },
    {
      key: "allowGuestMoveTopBottom",
      label: "Move to top / bottom",
      description: "Move a song to the top or bottom of the queue.",
      checked: !!allowGuestMoveTopBottom,
      onChange: (on) => handleUpdateSettings({ allowGuestMoveTopBottom: on }),
      serverPinOnly: true,
    },
  ];

  const playerToggles: SettingToggle[] = [
    {
      key: "showIdleScreen",
      label: "Idle screen",
      description: "Show a banner on remotes after a minute without activity.",
      checked: !!showIdleScreen,
      onChange: (on) => handleUpdateSettings({ showIdleScreen: on }),
    },
    {
      key: "askWhoSing",
      label: "Ask who's singing",
      description: "In karaoke mode, ask for the singer's name when adding a song.",
      checked: !!askWhoSing,
      onChange: (on) => handleUpdateSettings({ askWhoSing: on }),
    },
  ];

  const renderToggle = (toggle: SettingToggle) => (
    <Box key={toggle.key} sx={formControlSx}>
      <FormControlLabel
        control={
          <Switch
            checked={toggle.checked}
            onChange={(e) => toggle.onChange(e.target.checked)}
          />
        }
        label={toggle.label}
      />
      <Typography variant="caption">{toggle.description}</Typography>
    </Box>
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUpdateSettings = async (partialData: SetStateAction<any>) => {
    const updatedSettings = {
      ...defaultSettings,
      ...playerSettings,
      ...partialData,
    };

    try {
      if (Object.keys(partialData).includes("administratorOverride")) {
        setData("administratorOverride", partialData["administratorOverride"]);
      } else {
        await sendRequest(SocketEvent.UPDATE_PLAYER_SETTINGS, updatedSettings);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [newRoomId, setNewRoomID] = useState(roomId); // State for Room ID
  const [fontSize, setFontSize] = useState(data.fontSize); // State for Room ID

  const handleCloseModal = (
    event: object,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    setNewRoomID(roomId);
    modalProps.onClose && modalProps.onClose(event, reason);
  };

  const [showWarningModal, setShowWarningModal] = useState(false);

  // Function to handle Apply button click
  const handleApplyClick = () => {
    setShowWarningModal(true);
  };

  const handleCloseWarningModal = (confirm: boolean) => {
    if (confirm) {
      setData("roomId", newRoomId);
      // Apply changes
      console.log("Change Room to", newRoomId);
      const newUrl = `${window.location.pathname}?roomId=${encodeURIComponent(
        newRoomId
      )}`;
      window.location.replace(newUrl);
    }

    setShowWarningModal(false);
  };

  const hiddenDivRef = useRef(null);

  const handleBlurAll = () => {
    if (hiddenDivRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      hiddenDivRef.current?.blur();
    }
  };

  useEffect(() => {
    handleBlurAll();
  }, [modalProps.open]);

  const formControlSx = {
    paddingY: 2,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <Modal
        sx={{
          background: "rgba(0,0,0,.85)",
          maxHeight: "100%",
          overflowY: "auto",
          display: modalProps.open ? "block" : "none",
        }}
        keepMounted={true}
        {...modalProps}
        onClose={handleCloseModal}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Locked room: ask for the PIN first. Only while open, since the
              modal stays mounted and the pad listens to the keyboard. */}
          {modalProps.open &&
            admin.supported &&
            admin.hasAdminPassword &&
            !admin.isAdmin && (
              <AdminPinPad
                onClose={() => handleCloseModal({}, "escapeKeyDown")}
              />
            )}
          <Box
            ref={hiddenDivRef}
            tabIndex={-1}
            sx={{ width: 0, height: 0, overflow: "hidden" }}
          />
          <AppBar position="sticky" sx={{ background: "rgba(0,0,0,.9)" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="back"
                onClick={() => handleCloseModal({}, "escapeKeyDown")}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography>Settings</Typography>
            </Toolbar>
          </AppBar>
          <Container
            sx={{
              width: "100%",
              paddingX: 4,
              paddingY: 2,
            }}
          >
            {admin.supported && <AdminSection />}
            {admin.supported && admin.isAdmin && <DesktopControls />}
            <FormControl
              component="fieldset"
              disabled={admin.supported && !admin.isAdmin}
              sx={{ width: "100%" }}
            >
              <Typography variant="h5">Guest permissions</Typography>
              <Typography variant="caption">
                What everyone in the room can do, including whoever changes
                these settings. Admin devices (unlocked with the admin password,
                or Administrator Override on) can always do all of these.
              </Typography>
              <FormGroup>
                {guestToggles
                  .filter((toggle) => !toggle.serverPinOnly || admin.supported)
                  .map(renderToggle)}
                {/* Without server PIN support, admin is this per-device switch. */}
                {!admin.supported && (
                  <Box sx={formControlSx}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!administratorOverride}
                          onChange={(e) =>
                            handleUpdateSettings({
                              administratorOverride: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Administrator Override"
                    />
                    <Typography variant="caption">
                      Allows an administrator to bypass the restrictions set above
                      on this device.
                    </Typography>
                  </Box>
                )}
              </FormGroup>

              <Typography variant="h5" sx={{ marginTop: 4 }}>
                Player
              </Typography>
              <FormGroup>
                {playerToggles.map(renderToggle)}

                {false && (
                  <Box sx={formControlSx}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!autoRemovePlayedTracks}
                          onChange={(e) =>
                            handleUpdateSettings({
                              autoRemovePlayedTracks: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Auto-Remove Played Tracks"
                    />
                    <Typography variant="caption">
                      Automatically removes tracks once they've been played or
                      if the user advances to the next song.
                    </Typography>
                  </Box>
                )}

                {false && (
                  <Box sx={formControlSx}>
                    <FormControl>
                      <FormLabel>Skip & Delete Threshold</FormLabel>
                      <Slider
                        value={skipDeleteThreshold || 0}
                        onChange={(_e, value) =>
                          handleUpdateSettings({
                            skipDeleteThreshold: value as number,
                          })
                        }
                        step={10}
                        marks
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="caption">
                        Sets a threshold for automatic deletion of a track if
                        it's skipped after being played to a certain percentage.
                        Setting the value to 0 will delete all the time.
                      </Typography>
                    </FormControl>
                  </Box>
                )}
              </FormGroup>
            </FormControl>

            <Typography variant="h5" sx={{ marginTop: 4 }}>
              Room Settings
            </Typography>
            <FormControl
              component="fieldset"
              sx={{
                width: "100%",
              }}
            >
              <FormGroup>
                <Box sx={{ ...formControlSx, gap: 1, flexDirection: "row" }}>
                  <TextField
                    label="Room ID"
                    variant="outlined"
                    value={newRoomId}
                    onChange={(e) => setNewRoomID(e.target.value)}
                    //   helperText="Enter the unique identifier for the room.11"
                    fullWidth
                  />
                  <Button
                    sx={{}}
                    disabled={roomId === newRoomId}
                    variant="contained"
                    onClick={handleApplyClick}
                  >
                    Apply
                  </Button>
                </Box>
                <Box sx={{ ...formControlSx, gap: 1, flexDirection: "row" }}>
                  <TextField
                    label="Font Size"
                    variant="outlined"
                    type="number"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value, 10))}
                    //   helperText="Enter the unique identifier for the room.11"
                    fullWidth
                  />
                  <Button
                    sx={{}}
                    disabled={fontSize === data?.fontSize}
                    variant="contained"
                    onClick={() => {
                      setData("fontSize", fontSize);
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </FormGroup>
            </FormControl>
          </Container>
        </Box>
      </Modal>
      <Dialog
        open={showWarningModal}
        onClose={() => handleCloseWarningModal(false)}
      >
        <DialogTitle>Change Room ID?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing the Room ID may lose all the other settings. Are you sure
            you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleCloseWarningModal(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleCloseWarningModal(true)}
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsModal;
